/** The presentations a cue binding may name. The host owns every one; an activity names one. */

/** What a cue paints on one live node, on top of what the draw put there. */
export interface Pulse {
  /** A multiplier on the node's own opacity. */
  readonly alpha: number;
  /** A multiplier on the node's own size, about the centre of its box. */
  readonly scale: number;
}

export const IDENTITY: Pulse = { alpha: 1, scale: 1 };

export interface Presentation {
  readonly durationMs: number;
  /** The pulse at `t` in 0..1. `at(1)` is the identity, so a presentation cannot outlive itself. */
  readonly at: (t: number) => Pulse;
}

/** One smooth up-and-back over 0..1, peaking at the middle. */
function swell(t: number): number {
  return Math.sin(Math.PI * t);
}

/** A fast rise to `rise` and a squared decay after it: what a hit looks like. */
function hit(t: number, rise: number): number {
  if (t < rise) return t / rise;
  const after = (t - rise) / (1 - rise);
  return (1 - after) * (1 - after);
}

const IMPACT_RISE = 0.25;
const IMPACT_SCALE = 0.12;
const FLASH_DEPTH = 0.7;
const FADE_DEPTH = 0.9;

/**
 * A brief punch: the node grows about its own centre and settles.
 *
 * Size rather than brightness, because a `Container` can be dimmed and scaled but not brightened
 * without geometry the compositor does not have.
 */
const IMPACT: Presentation = {
  durationMs: 260,
  at: (t) => ({ alpha: 1, scale: 1 + IMPACT_SCALE * hit(t, IMPACT_RISE) }),
};

/** One blink: a single dip and return, never a repeating flicker. */
const FLASH: Presentation = {
  durationMs: 220,
  at: (t) => ({ alpha: 1 - FLASH_DEPTH * swell(t), scale: 1 }),
};

/** The same dip, deeper and slow enough to read as going away and coming back. */
const FADE: Presentation = {
  durationMs: 700,
  at: (t) => ({ alpha: 1 - FADE_DEPTH * swell(t), scale: 1 }),
};

const PRESENTATIONS = new Map<string, Presentation>([
  ["impact", IMPACT],
  ["flash", FLASH],
  ["fade", FADE],
]);

/** The presentations this build draws. `CueBinding.presentation` stays an open string. */
export const PRESENTATION_NAMES: readonly string[] = [...PRESENTATIONS.keys()];

/** What a binding named, or undefined for a presentation this build does not draw. */
export function presentationNamed(name: string): Presentation | undefined {
  return PRESENTATIONS.get(name);
}

/** The pulse `presentation` is at, `t` clamped so a late frame cannot overshoot into nothing. */
export function pulseAt(presentation: Presentation, t: number): Pulse {
  if (t <= 0) return presentation.at(0);
  if (t >= 1) return IDENTITY;
  return presentation.at(t);
}
